import { profile, travel, axiosConfig } from 'api';

import { Company } from 'models/company';
import { PaymentMethodResponseData, PaymentMethod, PaymentCardData, PaymentCard } from 'models/paymentCard';
import { Response } from 'models/response';

export const getGroupPaymentMethods = async (groupId: string): Promise<PaymentMethod[]> => {
  try {
    const response = await profile.get<Response<PaymentMethodResponseData>>(
      `/Payments/UserGroup/${groupId}/Payments`,
      axiosConfig,
    );
    return response.data.data.payment_methods;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getPaymentMethods = async (): Promise<PaymentMethod[]> => {
  try {
    const response = await profile.get<Response<PaymentMethodResponseData>>('/Payments/PaymentMethods', axiosConfig);
    return response.data.data.payment_methods;
  } catch (error) {
    return Promise.reject(error);
  }
};

interface ISpreedlyData {
  spreedly_data: {
    credit_card_data: ISpreedlyCreditCardData;
    tokenizer: string;
    email: string;
    document: IDocument;
  };
}

interface ISpreedlyCreditCardData {
  payment_method_token: string;
  is_debit: boolean;
}

interface IVGSData {
  vgs_data: {
    credit_card_data: IVGSCreditCardData;
    tokenizer: string;
    email: string;
    document: IDocument;
  };
}

interface IVGSCreditCardData {
  card_cvv: string;
  card_number: string;
  first_six_digits: string;
  last_four_digits: string;
  email: string;
  first_name: string;
  last_name: string;
  month: string;
  year: string;
  is_debit: true;
}

interface IDocument {
  document_value: string;
  document_type_id: string;
}

type IResponse = ISpreedlyResponse | IVGSResponse;

const paymentIsVGS = (payment: IResponse) => payment.tokenizer.toUpperCase() === 'VGS';

const formatPayment = (payment: IResponse, email: string, document: string) => {
  const doc = {
    document_value: document,
    document_type_id: '',
  };
  if (paymentIsVGS(payment)) {
    return {
      vgs_data: {
        credit_card_data: payment.credit_card_data as IVGSCreditCardData,
        tokenizer: payment.tokenizer,
        email: email,
        document: doc,
      },
    } as IVGSData;
  }
  return {
    spreedly_data: {
      credit_card_data: payment.credit_card_data as ISpreedlyCreditCardData,
      tokenizer: payment.tokenizer,
      email: email,
      document: doc,
    },
  } as ISpreedlyData;
};

interface ISpreedlyResponse {
  credit_card_data: ISpreedlyCreditCardData;
  tokenizer: string;
}

interface IVGSResponse {
  credit_card_data: IVGSCreditCardData;
  tokenizer: string;
}

export const addPaymentCard = async (
  payment: IResponse,
  email: string,
  document: string,
  groupId?: string,
): Promise<any> => {
  const data = formatPayment(payment, email, document);
  try {
    let serviceReq = '/Payments/Company/Cards';
    if (groupId) {
      serviceReq = `/Payments/UserGroup/${groupId}/Cards`;
    }
    const response = await profile.post<Response<any>>(serviceReq, data, axiosConfig);
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const changeDefaultCard = async (cardId: number): Promise<any> => {
  try {
    const response = await profile.put<Response<any>>(
      '/Payments/Company/Cards/Default',
      { CardId: cardId },
      axiosConfig,
    );
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getGroupAllPaymentCards = async (groupId: string): Promise<PaymentCard[]> => {
  try {
    const response = await profile.get<Response<PaymentMethodResponseData>>(
      `/Payments/UserGroup/${groupId}/Cards`,
      axiosConfig,
    );
    const { list_cards } = response.data.data;
    if (list_cards && list_cards.length > 0) return list_cards;
    return [];
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getAllPaymentCards = async (): Promise<PaymentCardData[]> => {
  try {
    const response = await profile.get<Response<PaymentCardData[]>>('Payments/Company/Cards', axiosConfig);
    return response.data.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const saveGroupsCard = async (cardId: number, groups: string[]): Promise<Response<any>> => {
  try {
    const response = await profile.put<Response<PaymentMethodResponseData>>(
      '/Payments/Company/Cards/Groups',
      { CardId: cardId, GroupsIds: groups },
      axiosConfig,
    );
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const removeGroupsCard = async (cardId: number, groupsId: string[]): Promise<Response<any>> => {
  try {
    const response = await profile.delete<Response<PaymentMethodResponseData>>('/Payments/Company/Cards/Groups', {
      data: { CardId: cardId, GroupsIds: groupsId },
    });
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const removeCard = async (cardId: number): Promise<Response<any>> => {
  try {
    const response = await profile.delete<Response<PaymentMethodResponseData>>(
      `/Payments/Company/Cards/${cardId}`,
      axiosConfig,
    );
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getTravelStatus = async (travelId: string): Promise<Response<any>> => {
  try {
    const response = await travel.get<Response<any>>(`/PaymentExecution/Travel/${travelId}`, axiosConfig);
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getCompanyLineCredit = async (companyId: string): Promise<Response<Company>> => {
  try {
    const response = await profile.get<Response<Company>>(`/Company/${companyId}`, axiosConfig);
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};
